import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { landingPathFor } from "@/lib/landingPath";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
export default function AuthCallback() {
  const navigate = useNavigate();
  const { setAuthedFromGoogle } = useAuth();
  const processed = useRef(false);
  const [msg, setMsg] = useState("Signing you in with Google…");
  const [sessionId, setSessionId] = useState(null);
  const [workspaces, setWorkspaces] = useState(null);   // array or null
  const [email, setEmail] = useState("");

  const completeLogin = async (data) => {
    // Backend already set the httpOnly `access_token` cookie in the
    // /auth/google/session response. No JS-visible token to store.
    if (data.org?.slug) localStorage.setItem("tf_org_slug", data.org.slug);
    // Sync auth state (user + workspace + role/branch defaults) BEFORE
    // navigating so the Protected route wrapper sees a truthy user
    // on the very first render of /dashboard — no flash back to /login.
    await setAuthedFromGoogle(data.user, data.org);
    toast.success(`Welcome, ${data.user.name?.split(" ")[0] || "friend"}!`);
    navigate(landingPathFor(data.user), { replace: true });
  };

  useEffect(() => {
    if (processed.current) return;
    processed.current = true;

    const hash = window.location.hash || "";
    const match = hash.match(/session_id=([^&]+)/);
    if (!match) {
      navigate("/login", { replace: true });
      return;
    }
    const sid = decodeURIComponent(match[1]);
    setSessionId(sid);
    window.history.replaceState({}, "", window.location.pathname);

    (async () => {
      try {
        const { data } = await api.post("/auth/google/session", { session_id: sid });
        if (data.needs_workspace_selection) {
          setWorkspaces(data.workspaces || []);
          setEmail(data.email || "");
          setMsg(`Multiple workspaces found for ${data.email}. Pick one to continue.`);
          return;
        }
        await completeLogin(data);
      } catch (e) {
        toast.error(formatApiError(e));
        setMsg("Sign-in failed. Returning to login…");
        setTimeout(() => navigate("/login", { replace: true }), 1800);
      }
    })();
  }, []);

  const pickWorkspace = async (slug) => {
    setMsg("Signing you in…");
    try {
      const { data } = await api.post("/auth/google/session", { session_id: sessionId, org_slug: slug });
      await completeLogin(data);
    } catch (e) {
      toast.error(formatApiError(e));
      setMsg("Could not sign in to that workspace.");
    }
  };

  if (workspaces) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4 bg-background">
        <Card className="w-full max-w-md" data-testid="workspace-picker">
          <CardHeader>
            <CardTitle className="text-xl">Choose a workspace</CardTitle>
            <p className="text-sm text-muted-foreground">
              <span className="font-medium">{email}</span> is a member of {workspaces.length} workspaces.
            </p>
          </CardHeader>
          <CardContent className="space-y-2">
            {workspaces.map(w => (
              <button
                key={w.slug}
                data-testid={`pick-workspace-${w.slug}`}
                onClick={() => pickWorkspace(w.slug)}
                className="w-full text-left rounded-md border px-3 py-2 hover:bg-accent transition"
              >
                <div className="flex items-center justify-between">
                  <div>
                    <div className="font-medium">{w.name}</div>
                    <div className="text-xs text-muted-foreground">t/{w.slug}</div>
                  </div>
                  <div className="flex flex-wrap gap-1 justify-end max-w-[50%]">
                    {(w.roles || []).map(r => (
                      <Badge key={r} variant="secondary" className="text-[10px] capitalize">{r}</Badge>
                    ))}
                  </div>
                </div>
              </button>
            ))}
            <Button variant="outline" className="w-full mt-2" onClick={() => navigate("/login", { replace: true })}>
              Cancel
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center">
      <div className="text-center space-y-3">
        <div className="h-10 w-10 rounded-full border-2 border-primary/20 border-t-primary animate-spin mx-auto" />
        <div className="text-sm text-muted-foreground">{msg}</div>
      </div>
    </div>
  );
}
